import { useEffect, useMemo, useState } from "react";

import { concepts_get } from "../../../../services/invoices/concepts";
import { calculate_invoice_get } from "../../../../services/invoices/invoices";

import type {
  BusinessPartner,
  ConceptMinimal,
  ProductoAgregado,
} from "../types/modernInvoiceForm";

/* ---------------- helpers ---------------- */

function safeNumber(input: any) {
  if (typeof input === "string") {
    // colapsa puntos repetidos y elimina caracteres no numéricos ni punto
    const cleaned = input.replace(/(\.){2,}/g, ".").replace(/[^\d.]/g, "");
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  const n = Number(input);
  return Number.isFinite(n) ? n : 0;
}

function toRateFraction(v: any) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return n > 1 ? n / 100 : n; // 16 -> 0.16, 0.16 -> 0.16
}

function getTaxRateRaw(t: any) {
  return (
    t?.tasaocuota ??
    t?.tasa_o_cuota ??
    t?.tasa_cuota ??
    t?.tasa_o_cuotan ??
    t?.tasa_cuotan ??
    t?.tasa ??
    null
  );
}

function normalizeTaxList(list?: any[] | null) {
  if (!Array.isArray(list)) return null;
  return list.map((t) => {
    const base = safeNumber(t?.base ?? 0);
    const tasa = toRateFraction(getTaxRateRaw(t));
    const importe =
      t?.importe != null
        ? safeNumber(t.importe)
        : tasa != null
        ? base * tasa
        : 0;

    return {
      base,
      importe,
      impuesto: t?.impuesto ?? "",
      tipofactor: t?.tipofactor ?? t?.tipo_factor ?? "",
      tasaocuota: tasa,
    };
  });
}

function normalizeConceptResponse(c: any, fb?: Partial<ConceptMinimal>) {
  const impuestos = c?.impuestos
    ? {
        traslados: normalizeTaxList(c.impuestos.traslados),
        retenciones: normalizeTaxList(c.impuestos.retenciones),
      }
    : null;

  console.log(c);
  console.log(fb);

  return {
    concept: fb?.concept ?? c?.concept ?? "",
    descripcion: c?.descripcion ?? fb?.descripcion ?? "",
    unidad: c?.unidad ?? fb?.unidad ?? "",
    valor_unitario:
      c?.valor_unitario ?? c?.valorunitario ?? fb?.valor_unitario ?? 0,
    cantidad: safeNumber(c?.cantidad ?? 1),
    descuento: c?.descuento ?? null,
    importe: c?.importe ?? null,
    objeto_imp: c?.objeto_imp ?? fb?.objeto_imp ?? null,
    clave_prod_serv:
      c?.clave_prod_serv ?? c?.claveprodserv ?? fb?.clave_prod_serv ?? "",
    clave_unidad: c?.clave_unidad ?? c?.claveunidad ?? fb?.clave_unidad ?? "",
    no_identificacion:
      c?.no_identificacion ??
      c?.noidentificacion ??
      fb?.no_identificacion ??
      "",
    impuestos,
    implocales: c?.implocales ?? null,
  };
}

function buildImpuestoPreview(c?: any): string {
  if (!c) return "";
  const mapNombre: Record<string, string> = {
    "001": "ISR",
    "002": "IVA",
    "003": "IEPS",
  };
  const partes: string[] = [];

  const tras = c?.impuestos?.traslados;
  if (Array.isArray(tras)) {
    tras.forEach((t: any) => {
      const imp = mapNombre[t?.impuesto] || t?.impuesto || "";
      const tasa = toRateFraction(getTaxRateRaw(t));
      // tasa ya normalizada a fracción (0.16), mostrar como 16%
      const tasaPct =
        tasa == null || !Number.isFinite(Number(tasa))
          ? "0%"
          : `${(Number(tasa) * 100).toString().replace(/\.?0+$/, "")}%`;
      if (imp) partes.push(`${imp}(${tasaPct})`);
    });
  }

  const locTras = c?.implocales?.traslados;
  if (Array.isArray(locTras) && locTras.length > 0) {
    const ish = locTras.find(
      (l: any) => (l?.implocaltrasladado || l?.nombre)?.toUpperCase() === "ISH"
    );
    if (ish) partes.push(`ISH(${ish?.importe ?? "0.0000"})`);
  }

  return partes.join(" ");
}

type Notify = (
  text: string,
  severity?: "warning" | "info" | "error" | "success"
) => void;

/* ---------------- hook ---------------- */

export function useProductsSection(args: {
  selectedCliente: BusinessPartner | null;
  notify: Notify;
}) {
  const { selectedCliente, notify } = args;

  // ------- estado que consume <Products/> -------
  const [conceptosList, setConceptosList] = useState<ConceptMinimal[]>([]);
  const [selectedConcept, setSelectedConcept] = useState<ConceptMinimal | null>(
    null
  );
  const [cantidad, setCantidad] = useState<number>(1);
  const [precio, setPrecio] = useState<string>("");
  const [unidad, setUnidad] = useState<string>("");
  const [descripcion, setDescripcion] = useState<string>("");

  const [productosAgregados, setProductosAgregados] = useState<
    ProductoAgregado[]
  >([]);
  const [addingConcept, setAddingConcept] = useState(false);
  const [impuestoPreview, setImpuestoPreview] = useState<string>("");

  // preview de impuestos del concepto seleccionado
  useEffect(() => {
    setImpuestoPreview(buildImpuestoPreview(selectedConcept as any));
  }, [selectedConcept]);

  // catálogo de conceptos
  useEffect(() => {
    (async () => {
      try {
        const dt = await concepts_get();
        const rows = Array.isArray(dt?.data) ? dt.data : [];
        setConceptosList(
          rows.map((c: any) => ({
            concept: c.concept,
            descripcion: c.descripcion,
            unidad: c.unidad,
            valor_unitario: safeNumber(c.valor_unitario),
            clave_prod_serv: c.clave_prod_serv,
            clave_unidad: c.clave_unidad,
            no_identificacion: c.no_identificacion,
            objeto_imp: c.objeto_imp,
            impuestos: c.impuestos ?? null,
            implocales: c.implocales ?? null,
          }))
        );
      } catch {
        setConceptosList([]);
      }
    })();
  }, []);

  // “setSelectedConcept” que además precarga campos (igual que hacías en el padre)
  const selectConceptAndPrefill = (c: ConceptMinimal | null) => {
    setSelectedConcept(c);
    setDescripcion(c?.descripcion || "");
    setUnidad(c?.unidad || "");
    setPrecio(c?.valor_unitario != null ? String(c.valor_unitario) : "");
  };

  // agregar concepto (misma lógica + sanitizado y normalización)
  const agregarConcepto = async () => {
    if (!selectedCliente) {
      notify(
        "Selecciona primero un socio de negocios para poder calcular el concepto",
        "warning"
      );
      return;
    }
    if (!selectedConcept) return;
    if (addingConcept) return;

    const precioNum = safeNumber(precio || selectedConcept.valor_unitario || 0);
    if (!Number.isFinite(precioNum) || precioNum <= 0) return;

    setAddingConcept(true);
    try {
      const payload = {
        person_type: selectedCliente.person_type ?? null,
        concepto: {
          concept: selectedConcept.concept,
          descripcion: descripcion || selectedConcept.descripcion || "",
          clave_prod_serv: selectedConcept.clave_prod_serv,
          clave_unidad: selectedConcept.clave_unidad,
          unidad: unidad || selectedConcept.unidad || "",
          no_identificacion: selectedConcept.no_identificacion,
          valor_unitario: precioNum,
          cantidad: safeNumber(cantidad || 1),
          descuento: null,
          importe: null,
          peso_en_kg: null,
          objeto_imp: selectedConcept.objeto_imp ?? "02",
          impuestos: selectedConcept.impuestos ?? null,
          implocales: selectedConcept.implocales ?? null,
        },
      };

      const dt = await calculate_invoice_get(payload);
      const raw = dt?.data;
      // console.log(dt);

      // console.log(raw);
      const n = normalizeConceptResponse(raw, selectedConcept as any);

      const agregado: ProductoAgregado = {
        concepto: {
          concept: n.concept,
          descripcion: n.descripcion,
          unidad: n.unidad,
          valor_unitario: safeNumber(n.valor_unitario),
        },
        cantidad: safeNumber(n.cantidad),
        unidad: n.unidad,
        precio: String(n.valor_unitario),
        descripcion: n.descripcion,
        clave_prod_serv: n.clave_prod_serv,
        clave_unidad: n.clave_unidad,
        no_identificacion: n.no_identificacion,
        importe: n.importe ?? null,
        descuento: n.descuento ?? null,
        objeto_imp: n.objeto_imp ?? null,
        impuestos: n.impuestos ?? null,
        implocales: n.implocales ?? null,
      };

      console.log(agregado);
      setProductosAgregados((prev) => [...prev, agregado]);

      // limpiar inputs
      setSelectedConcept(null);
      setCantidad(1);
      setUnidad("");
      setPrecio("");
      setDescripcion("");
    } catch (err: any) {
      notify(
        err.response.data?.errorMessage.es ||
          "Ocurrió un error al calcular el concepto. Intenta nuevamente.",
        "error"
      );
    } finally {
      setAddingConcept(false);
    }
  };

  const eliminarConcepto = (idx: number) =>
    setProductosAgregados((p) => p.filter((_, i) => i !== idx));

  const limpiarConceptos = () => setProductosAgregados([]);

  // util para el padre cuando “reinicia” el documento
  const resetProducts = () => {
    setProductosAgregados([]);
    setSelectedConcept(null);
    setCantidad(1);
    setPrecio("");
    setUnidad("");
    setDescripcion("");
    setImpuestoPreview("");
  };

  const loadProducts = (rows: ProductoAgregado[]) => {
    setProductosAgregados(Array.isArray(rows) ? rows : []);
    setSelectedConcept(null);
    setCantidad(1);
    setPrecio("");
    setUnidad("");
    setDescripcion("");
    setImpuestoPreview("");
  };

  return {
    // catálogo y selección
    conceptosList,
    selectedConcept,
    setSelectedConcept: selectConceptAndPrefill,

    // inputs
    cantidad,
    setCantidad,
    precio,
    setPrecio,
    unidad,
    setUnidad,
    descripcion,
    setDescripcion,

    // colección
    productosAgregados,
    agregarConcepto,
    eliminarConcepto,
    limpiarConceptos,
    addingConcept,

    // UI
    impuestoPreview,

    // para el padre
    resetProducts,
    loadProducts,
  };
}
